import { Gender, HeartRateZone,
         IBodyDetails, IHeartRate, ILiveMetric, IMetricDebug, IMetricResult } from 'fitbeat.models';
import { CardioOrResistance } from '../entities';

interface IHeartRateWithDuration extends IHeartRate {
  durationToNext: number;
  zone: HeartRateZone;
}

interface IHeartRateAggregation {
  heartRatePoints: IHeartRateWithDuration[];
  sumOfHeartRates: number;
  timePerHeartRateZone: number[];
}

interface IMetricLoadDetails {
  load: number;
  timePerHeartRateZone: number[];
}

const defaultMaxHeartRate = 180;

const maleCalorieFactors = {
  adjustment: -55.0969,
  heartRate: 0.6309,
  weight: 0.1988,
  age: 0.2017,
};

const femaleCalorieFactors = {
  adjustment: -20.4022,
  heartRate: 0.4472,
  weight: -0.1263,
  age: 0.074,
};

export function calculateLatesMetrics(heartRatePoint: IHeartRate, bodyDetails: IBodyDetails): ILiveMetric {
  const { heartRate } = heartRatePoint;
  return {
    type: 'live',
    latestEffort: calculateHeartRatePercentageOfMax(heartRate, bodyDetails),
    latestHeartRate: heartRate,
    latestHeartRateZone: calculateHeartRateZoneOnly(heartRate, bodyDetails),
  };
}

export function calculateMetrics(bodyDetails: IBodyDetails,
                                 rawHeartRatePoints: IHeartRate[],
                                 cardioOrResistance: CardioOrResistance,
                                 workoutCalories?: number,
                                 debug: boolean = true,
                                ): IMetricResult | undefined {
  if (rawHeartRatePoints.length === 0) {
    return;
  }

  const startTime = Date.now();
  bodyDetails.heartRateMax = bodyDetails.heartRateMax || defaultMaxHeartRate;

  const aggregation = performAggregateCalculations(rawHeartRatePoints, bodyDetails.heartRateMax);
  const {
    heartRatePoints,
    sumOfHeartRates,
    timePerHeartRateZone,
  } = aggregation;

  const count = heartRatePoints.length;
  const latest = heartRatePoints[count - 1];
  const totalMinutes = (latest.timestamp.getTime() - heartRatePoints[0].timestamp.getTime()) / 60000;

  const latestHeartRate = latest.heartRate;
  const latestHeartRatePercentageOfMax = calculateHeartRatePercentageOfMax(latestHeartRate, bodyDetails);
  const latestHeartRateZone = latest.zone;

  const averageHeartRate = Math.round(sumOfHeartRates / count);
  const importWorkoutCalories = workoutCalories ? parseFloat(workoutCalories.toFixed(2)) : undefined;
  const calories = importWorkoutCalories ? importWorkoutCalories : calculateCaloriesBasedTimeBetweenPoints(
    rawHeartRatePoints,
    bodyDetails);

  const loadDetails = calculateLoad(timePerHeartRateZone, cardioOrResistance);
  const { load } = loadDetails;

  const averageHeartRatePercentageOfMax = Math.trunc((averageHeartRate / bodyDetails.heartRateMax) * 100);

  function buildDebugDetails(): IMetricDebug {
    return {
      totalMinutes,
      calculationTime: Date.now() - startTime,
      timePerHeartRateZone: loadDetails.timePerHeartRateZone,
      bodyDetails: {
        age: bodyDetails.age,
        gender: bodyDetails.gender,
        heartRateMax: bodyDetails.heartRateMax,
        height: bodyDetails.height,
        weight: bodyDetails.weight,
      },
    };
  }

  return {
    type: 'calculated',
    latestHeartRate,
    averageHeartRate,
    latestHeartRateZone,
    latestEffort: latestHeartRatePercentageOfMax,
    averageEffort: averageHeartRatePercentageOfMax,
    calories,
    load,
    latestReadingTimestamp: latest.timestamp,
    calculatedAt: new Date(),
    debug: debug ? buildDebugDetails() : undefined,
  };
}

function performAggregateCalculations(heartRates: IHeartRate[], maxHeartRate: number): IHeartRateAggregation {
  const lastItemIndex = heartRates.length - 1;

  function getDurationToTheNextItemOrZeroIfThisIsTheLastItem(index: number, current: IHeartRate): number {
    if (index === lastItemIndex) {
      return 0;
    }

    const nextTimestamp = heartRates[index + 1].timestamp.getTime();
    const currentTimestamp = current.timestamp.getTime();
    return nextTimestamp - currentTimestamp;
  }

  const heartRatePoints = [];
  let sumOfHeartRates = 0;
  const timePerHeartRateZone = [0, 0, 0, 0, 0, 0];
  for (let index = 0; index < heartRates.length; ++index) {
    const current = heartRates[index];
    sumOfHeartRates += current.heartRate;

    const durationToNext = getDurationToTheNextItemOrZeroIfThisIsTheLastItem(index, current);
    const zone = calculateHeartRateZoneOnly(current.heartRate, {
      heartRateMax: maxHeartRate,
    });

    timePerHeartRateZone[zone] += durationToNext;

    heartRatePoints.push({
      durationToNext,
      zone,
      ...current,
    });
  }

  return {
    heartRatePoints,
    sumOfHeartRates,
    timePerHeartRateZone,
  };
}

export function calculateHeartRateZoneOnly(heartRate: number, bodyDetails: Partial<IBodyDetails>): HeartRateZone {
  const maxHeartRate = bodyDetails.heartRateMax || defaultMaxHeartRate;
  const ofMax = heartRate / maxHeartRate;

  if (ofMax < 0.5) {
    return HeartRateZone.none;
  } else if (ofMax < 0.6) {
    return 1;
  } else if (ofMax < 0.7) {
    return 2;
  } else if (ofMax < 0.8) {
    return 3;
  } else if (ofMax < 0.9) {
    return 4;
  } else {
    return 5;
  }
}

export function calculateHeartRatePercentageOfMax(latestHeartRate: number, bodyDetails: IBodyDetails): number {
  const latestHeartRateOfMax = latestHeartRate / (bodyDetails.heartRateMax || defaultMaxHeartRate);
  return Math.trunc(latestHeartRateOfMax * 100);
}

export function calculateCaloriesBasedTimeBetweenPoints(
  heartRatePoints: IHeartRate[],
  bodyDetails: IBodyDetails): number | undefined {
  if (!bodyDetails.weight || !bodyDetails.age) {
    return undefined;
  }

  const calculateCaloriesForMs = (heartRate: number, ms: number) => {
    const caloriesPerMinuteAverage = calculateCaloriesPerMinuteAverage(
      bodyDetails.gender,
      heartRate,
      bodyDetails.weight,
      bodyDetails.age,
    );
    if (caloriesPerMinuteAverage < 0) {
      return 0;
    }
    const caloriesPerMillisecond = caloriesPerMinuteAverage / 60000;
    return caloriesPerMillisecond * ms;
  };

  const calories = heartRatePoints.reduce((totalCalories: number, heartRatePoint: IHeartRate, index: number) => {
    const previousEntry = heartRatePoints[index - 1];
    if ( !previousEntry || Number.isNaN(previousEntry.heartRate)
      || !heartRatePoint || Number.isNaN(heartRatePoint.heartRate)) {
      return totalCalories;
    }
    const timeDiffBetweenNowAndLast = heartRatePoint.timestamp.getTime() - previousEntry.timestamp.getTime();
    if (!timeDiffBetweenNowAndLast || Number.isNaN(timeDiffBetweenNowAndLast) || timeDiffBetweenNowAndLast < 0) {
      return totalCalories;
    }
    return totalCalories + calculateCaloriesForMs(previousEntry.heartRate, timeDiffBetweenNowAndLast);
  }, 0);
  return Math.max(0, Math.round(calories));
}

export function calculateCaloriesPurelyBasedOnPoints(
  heartRatePoints: IHeartRate[],
  bodyDetails: IBodyDetails,
): number | undefined {
  if (!bodyDetails.weight || !bodyDetails.age) {
    return undefined;
  }
  const calories = heartRatePoints.reduce(
    (totalCalories: any, heartRatePoint: IHeartRate) => {
      const caloriesPerMinuteAverage = calculateCaloriesPerMinuteAverage(
        bodyDetails.gender,
        heartRatePoint.heartRate,
        bodyDetails.weight,
        bodyDetails.age,
      );
      if (caloriesPerMinuteAverage < 0) {
        return totalCalories;
      }
      return totalCalories + caloriesPerMinuteAverage / 60;
    },
    0,
  );
  return Math.round(calories);
}

export function calculateCaloriesPerMinuteAverage(gender: Gender, heartRate: number, weight: number, age: number) {
  const factors = gender ===
  Gender.Male ? maleCalorieFactors : femaleCalorieFactors;
  const heartRateFactor = factors.heartRate * heartRate;
  const weightFactor = factors.weight * weight;
  const ageFactor = factors.age * age;

  return (factors.adjustment + heartRateFactor + weightFactor + ageFactor) / 4.184;
}

function calculateLoad(timePerZone: number[], cardioOrResistance: CardioOrResistance): IMetricLoadDetails {
  const buildZoneToEffortPoints = {
    0:1,
    1:2,
    2:3.5,
    3:5,
    4:3.5,
    5:2,
  };
  const burnZoneToEffortPoints = {
    0:1,
    1:1.5,
    2:2,
    3:3,
    4:4,
    5:5,
  };
  const balanceZoneToEffortPoints = {
    0:1.5,
    1:3,
    2:3.5,
    3:3.5,
    4:3.5,
    5:3.5,
  };
  const effortPointsForMinutesInZone = {
    [CardioOrResistance.Cardio] : burnZoneToEffortPoints,
    [CardioOrResistance.Resistance] : buildZoneToEffortPoints,
    [CardioOrResistance.Balance] : balanceZoneToEffortPoints,
  };
  const unroundedLoad = timePerZone
    .map((msec: number) => msec / 60000)
    .reduce((acc: number, minutesInZone: number, zone: number) => {
      return acc + (minutesInZone * effortPointsForMinutesInZone[cardioOrResistance][zone]);
    }, 0);

  return {
    load: Math.round(unroundedLoad),
    timePerHeartRateZone: timePerZone,
  };
}

export function analyseHeartRateData(heartRatePoints: IHeartRate[]) {
  const assumedDisconnectLatencyMs = 10000;

  return heartRatePoints.reduce((result: any, currentValue: IHeartRate) => {
    if (!result.previousValue) {
      return {
        ...result,
        previousValue: currentValue,
      };
    }

    const heartRateReceiveLatency = currentValue.timestamp.getTime() - result.previousValue.timestamp.getTime();
    result.worstLatency = heartRateReceiveLatency > result.worstLatency ? heartRateReceiveLatency : result.worstLatency;
    result.bestLatency = heartRateReceiveLatency < result.bestLatency ? heartRateReceiveLatency : result.bestLatency;
    result.previousValue = currentValue;

    if (heartRateReceiveLatency > assumedDisconnectLatencyMs) {
      result.potentialDisconnectsAt.push(currentValue);
    }

    return result;
  }, {
    worstLatency: 0,
    bestLatency: 99999,
    potentialDisconnectsAt: [],
  } as any);
}
